import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { Link, Route } from 'react-router-dom';
import { LayoutPageGrid } from 'ui/LayoutGrid/LayoutGrid';
import { usePermissionPage } from 'hooks';
import { useMeContext } from 'components';
import {
  ClientApplication,
  VersionPropsFragment,
  useClientVersionSummariesQuery,
  useClientVersionsQuery,
  useCheckClientVersionQuery,
} from 'schema/serverTypes';
import { CreateVersionPage } from '../CreateVersionPage';
import {
  compareVersions,
  formatVersion,
  parseVersion,
  platformLabel,
  VersionNumber,
} from '../versionUtils';

const platforms = [ClientApplication.Android, ClientApplication.Ios];

const VersionList = ({ versions }: { versions: VersionPropsFragment[] }) => (
  <List dense disablePadding>
    {versions.map((version) => (
      <ListItem key={version.id} disableGutters>
        <ListItemText
          primary={formatVersion(version)}
          secondary={version.mandatory ? 'Отмечена как обязательная' : 'Без отметки обязательности'}
        />
      </ListItem>
    ))}
  </List>
);

const CheckResult = ({
  client,
  version,
}: {
  client: ClientApplication;
  version: VersionNumber;
}) => {
  const { data, loading, error, refetch } = useCheckClientVersionQuery({
    variables: { client, ...version },
    // Mandatory здесь вычислен для установленной версии и не должен менять флаг записи в cache.
    fetchPolicy: 'no-cache',
  });
  if (loading)
    return (
      <Box role="status" my={1}>
        <CircularProgress size={20} /> Проверяем версию…
      </Box>
    );
  if (error)
    return (
      <Alert severity="error">
        Не удалось проверить версию.{' '}
        <Button
          onClick={() => {
            void refetch();
          }}
        >
          Повторить
        </Button>
      </Alert>
    );
  if (!data) return null;
  const result = data.latestClientVersion;
  return (
    <Alert severity={!result ? 'success' : result.mandatory ? 'warning' : 'info'}>
      {!result
        ? 'Обновление не требуется: более новая версия не зарегистрирована.'
        : `${result.mandatory ? 'Обязательное' : 'Необязательное'} обновление до ${formatVersion(
            result
          )}.`}
    </Alert>
  );
};

const VersionChecker = ({ client }: { client: ClientApplication }) => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [submitted, setSubmitted] = useState<{ value: string; attempt: number } | null>(null);
  const parsed = parseVersion(input);
  const panelId = `version-check-${client}`;
  return (
    <Box mt={2}>
      <Button aria-expanded={open} aria-controls={panelId} onClick={() => setOpen(!open)}>
        Проверить установленную версию
      </Button>
      <Collapse in={open} unmountOnExit>
        <Box id={panelId} mt={1}>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (parsed) setSubmitted({ value: input, attempt: (submitted?.attempt || 0) + 1 });
            }}
          >
            <TextField
              fullWidth
              id={`installed-version-${client}`}
              label="Установленная версия"
              placeholder="1.2.3"
              value={input}
              onChange={(event) => {
                setInput(event.target.value);
                setSubmitted(null);
              }}
              error={input.length > 0 && !parsed}
              helperText="Три целых неотрицательных числа: major.minor.patch"
              inputProps={{ maxLength: 32 }}
            />
            <Box my={1}>
              <Button type="submit" variant="outlined" disabled={!parsed}>
                Проверить
              </Button>
            </Box>
          </form>
          {submitted?.value === input && parsed && (
            <CheckResult key={submitted.attempt} client={client} version={parsed} />
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

const PlatformHistory = ({
  client,
  onClose,
}: {
  client: ClientApplication;
  onClose: () => void;
}) => {
  const [moreError, setMoreError] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const { data, loading, error, refetch, fetchMore } = useClientVersionsQuery({
    variables: { client, first: 20 },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });
  const connection = data?.clientVersions;
  const loadMore = async () => {
    if (!connection?.pageInfo.endCursor || loadingMore) return;
    setLoadingMore(true);
    setMoreError(false);
    try {
      await fetchMore({
        variables: { client, first: 20, after: connection.pageInfo.endCursor },
        updateQuery: (previous, { fetchMoreResult }) => {
          if (!fetchMoreResult?.clientVersions) return previous;
          return {
            ...fetchMoreResult,
            clientVersions: {
              ...fetchMoreResult.clientVersions,
              nodes: [
                ...(previous.clientVersions?.nodes || []),
                ...(fetchMoreResult.clientVersions.nodes || []),
              ],
            },
          };
        },
      });
    } catch {
      setMoreError(true);
    } finally {
      setLoadingMore(false);
    }
  };
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="version-history-title">
      <DialogTitle id="version-history-title">Вся история — {platformLabel(client)}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="textSecondary">
          Зарегистрированные версии от новых к старым. Отметка относится к записи, а не к
          установленной версии.
        </Typography>
        {loading && (
          <Box role="status" my={2}>
            <CircularProgress size={24} /> Загрузка истории…
          </Box>
        )}
        {error ? (
          <Alert severity="error">
            Не удалось загрузить историю.{' '}
            <Button
              onClick={() => {
                void refetch();
              }}
            >
              Повторить
            </Button>
          </Alert>
        ) : (
          connection && (
            <>
              <Typography variant="body2">Всего записей: {connection.totalCount}</Typography>
              {connection.nodes?.length ? (
                <VersionList
                  versions={connection.nodes.filter((item): item is VersionPropsFragment => !!item)}
                />
              ) : (
                <Typography>Версии ещё не зарегистрированы.</Typography>
              )}
              {moreError && (
                <Alert severity="error">
                  Не удалось загрузить следующую страницу. Уже загруженные записи сохранены.
                </Alert>
              )}
              {connection.pageInfo.hasNextPage && (
                <Button
                  disabled={loadingMore}
                  onClick={() => {
                    void loadMore();
                  }}
                >
                  {loadingMore ? 'Загрузка…' : moreError ? 'Повторить загрузку' : 'Показать ещё'}
                </Button>
              )}
            </>
          )
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Закрыть</Button>
      </DialogActions>
    </Dialog>
  );
};

export const VersionsPage = () => {
  const { user } = useMeContext();
  usePermissionPage('/wards', user?.permissions.clientVersions.manage);
  const { data, loading, error, refetch } = useClientVersionSummariesQuery({
    fetchPolicy: 'network-only',
  });
  const [historyClient, setHistoryClient] = useState<ClientApplication | null>(null);
  return (
    <LayoutPageGrid title="Версии клиентов">
      <Box width="100%" minWidth={0}>
        <Box mb={2}>
          <Typography color="textSecondary">
            Зарегистрированные версии и правила обновления. Эти данные не подтверждают публикацию в
            магазинах и не показывают версии на устройствах пользователей.
          </Typography>
        </Box>
        {loading && (
          <Box role="status" mb={2}>
            <CircularProgress size={24} /> Загрузка версий…
          </Box>
        )}
        {error && (
          <Box mb={2}>
            <Alert severity="error">
              Не удалось загрузить версии.{' '}
              <Button
                onClick={() => {
                  void refetch();
                }}
              >
                Повторить
              </Button>
            </Alert>
          </Box>
        )}
        <Grid container spacing={2}>
          {platforms.map((client) => {
            const summary =
              !error && !loading
                ? data?.clientVersionSummaries.find((item) => item.client === client)
                : undefined;
            const latest = summary?.latestVersion;
            const mandatory = summary?.mandatoryVersion;
            return (
              <Grid item xs={12} md={6} key={client}>
                <Card style={{ height: '100%', minWidth: 0 }}>
                  <CardContent>
                    <Typography variant="h4" component="h2">
                      {platformLabel(client)}
                    </Typography>
                    <Box my={2}>
                      <Button
                        component={Link}
                        to={`/versions/add?client=${client}`}
                        variant="contained"
                        color="primary"
                      >
                        Добавить версию
                      </Button>
                    </Box>
                    {summary ? (
                      latest ? (
                        <>
                          <Typography color="textSecondary">
                            Последняя зарегистрированная версия
                          </Typography>
                          <Typography variant="h2" component="p">
                            {formatVersion(latest)}
                          </Typography>
                          <Box my={2}>
                            <Typography variant="subtitle2">Правило обновления</Typography>
                            <Typography>
                              {mandatory
                                ? `Обновление обязательно для версий ниже ${formatVersion(
                                    mandatory
                                  )}.`
                                : 'Обязательное обновление не задано.'}
                            </Typography>
                            {(!mandatory || compareVersions(mandatory, latest) < 0) && (
                              <Typography>
                                {mandatory
                                  ? `Для версий от ${formatVersion(mandatory)} до ${formatVersion(
                                      latest
                                    )} (не включая ${formatVersion(
                                      latest
                                    )}) обновление необязательное.`
                                  : `Для версий ниже ${formatVersion(
                                      latest
                                    )} обновление необязательное.`}
                              </Typography>
                            )}
                            <Typography variant="body2" color="textSecondary">
                              Порог — максимальная версия с отметкой обязательности.
                            </Typography>
                          </Box>
                          <Typography variant="subtitle2">Предыдущие записи</Typography>
                          {summary.previousVersions.length ? (
                            <VersionList versions={summary.previousVersions} />
                          ) : (
                            <Typography variant="body2">Предыдущих записей нет.</Typography>
                          )}
                        </>
                      ) : (
                        <Typography>
                          Версии ещё не зарегистрированы. Правила обновления не заданы.
                        </Typography>
                      )
                    ) : !loading && !error ? (
                      <Alert severity="error">
                        Сервер не вернул данные платформы.{' '}
                        <Button
                          onClick={() => {
                            void refetch();
                          }}
                        >
                          Повторить
                        </Button>
                      </Alert>
                    ) : null}
                    {summary && (
                      <Box mt={1}>
                        <Button onClick={() => setHistoryClient(client)}>
                          Вся история ({summary.totalCount})
                        </Button>
                      </Box>
                    )}
                    <VersionChecker client={client} />
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
        {historyClient && (
          <PlatformHistory
            key={historyClient}
            client={historyClient}
            onClose={() => setHistoryClient(null)}
          />
        )}
        <Route exact path="/versions/add" component={CreateVersionPage} />
      </Box>
    </LayoutPageGrid>
  );
};
